import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { useAssetLookup } from "../../api/useAssetLookup";
import type { AnalysisDetails } from "../../model/Analysis";
import type { Finding } from "../../model/Finding";
import { sectionMetrics } from "../../model/sectionMetrics";
import { AllPurchases } from "./AllPurchases";
import { BuildAdviceCard } from "./BuildAdviceCard";
import { BuildCard } from "./BuildCard";
import { BuyTakeaway } from "./BuyTakeaway";
import { CoreBuildSouls } from "./CoreBuildSouls";
import { CoreBuildTable } from "./CoreBuildTable";
import { MetricRows } from "./MetricRows";
import { OtherFindings } from "./OtherFindings";

const CAPTION = msg({
  id: "analysis.core.souls_caption",
  message: "Key items: souls when bought, yours and usual",
});
const CAPTION_BUILD = msg({
  id: "analysis.core.souls_caption_build",
  message: "Key items of this build: souls when bought, yours and usual",
});
const CAPTION_NONE_BOUGHT = msg({
  id: "analysis.core.souls_caption_none",
  message: "This is how strong {hero} players build: key items — you have none of them",
});

// Находки, после которых «по этой сборке закуп в порядке» уже не сказать.
const BUILD_ISSUES = new Set(["item_bought_late", "core_item_missing", "off_build_early"]);

// Подпись к ключевым предметам по сборке игрока.
const useCoreCaption = (details: AnalysisDetails, heroId: number): string => {
  const { _ } = useLingui();
  const hero = useAssetLookup("heroes")(heroId);
  if (details.build?.outcome === "alternative") {
    return _(CAPTION_BUILD);
  }
  if (details.core_build.every((item) => item.bought_at_s === null)) {
    return _({ ...CAPTION_NONE_BOUGHT, values: { hero: hero.name } });
  }
  return _({ ...CAPTION, values: { hero: hero.name } });
};

const hasBuildIssues = (findings: readonly Finding[]): boolean =>
  findings.some((finding) => BUILD_ISSUES.has(finding.code) && finding.severity !== "good");

type BuyDetailsProps = {
  details: AnalysisDetails;
  findings: readonly Finding[];
  heroId: number;
};

// Закуп с ключевыми предметами: сборка игрока, совет сборки из игры («Попробуй сборку
// сильных», если закуп хуже, чем у большинства сильных, и матч не сыгран сильно), ключевые
// предметы (по душам к покупке — полосами, у разборов до 01.11.2026 — по времени), вывод,
// находки, метрики и все покупки.
export const BuyDetails = ({ details, findings, heroId }: BuyDetailsProps) => {
  const caption = useCoreCaption(details, heroId);
  const metrics = sectionMetrics(details, "buy");
  const bySouls = details.core_build.some((item) => item.souls_p50 !== null);
  return (
    <>
      {details.build != null && (
        <BuildCard
          build={details.build}
          heroId={heroId}
          purchases={details.purchases}
          fine={!hasBuildIssues(findings)}
        />
      )}
      {details.build_advice != null && (
        <BuildAdviceCard advice={details.build_advice} heroId={heroId} />
      )}
      {bySouls ? (
        <CoreBuildSouls items={details.core_build} caption={caption} />
      ) : (
        <CoreBuildTable items={details.core_build} heroId={heroId} />
      )}
      <BuyTakeaway findings={findings} heroId={heroId} />
      <OtherFindings
        findings={findings}
        shown={[
          "item_bought_late",
          "core_item_missing",
          "core_items_on_time",
          ...(metrics.length > 0 ? (["metric"] as const) : []),
        ]}
        hasContent
      />
      <MetricRows metrics={metrics} />
      <AllPurchases purchases={details.purchases} heroId={heroId} />
    </>
  );
};
